import React from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";
import { motion } from "framer-motion";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useScrollLock } from "./lenisContext";

const MotionBox = motion(Box);

export const BACKDROP_OVERLAY_Z_INDEX = 1500;
export const DIALOG_OVERLAY_TOP_OFFSET = {
    xs: "max(1rem, env(safe-area-inset-top, 0px))",
    sm: "max(2rem, env(safe-area-inset-top, 0px))",
};

const DEFAULT_TRANSITION = {
    opacity: {
        duration: 0.38,
        ease: [0.16, 1, 0.3, 1],
    },
    backdropFilter: {
        duration: 0.38,
        ease: [0.16, 1, 0.3, 1],
    },
};

export default function BackdropOverlay({
    open = false,
    onClick,
    children,
    mountWhenClosed = false,
    zIndex = BACKDROP_OVERLAY_Z_INDEX,
    sx,
    contentSx,
    transition = DEFAULT_TRANSITION,
}) {
    useLockBodyScroll(open);
    useScrollLock(open);

    if (!open && !mountWhenClosed) {
        return null;
    }

    if (typeof document === "undefined") {
        return null;
    }

    const handleBackdropClick = (event) => {
        if (!onClick) return;
        if (event.target !== event.currentTarget) return;
        onClick(event);
    };

    return createPortal(
        <MotionBox
            aria-hidden={open ? undefined : true}
            onClick={handleBackdropClick}
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{
                opacity: open ? 1 : 0,
                backdropFilter: open ? "blur(8px)" : "blur(0px)",
            }}
            transition={transition}
            sx={{
                position: "fixed",
                inset: 0,
                zIndex,
                overflow: "hidden",
                overscrollBehavior: "none",
                cursor: onClick ? "default" : undefined,
                pointerEvents: open ? "auto" : "none",
                backgroundColor: "rgba(0,0,0,0.38)",
                backgroundImage:
                    "radial-gradient(circle at right, rgba(255,190,90,0.06), transparent 45%)",
                ...(children
                    ? {
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          px: 2,
                          pt: DIALOG_OVERLAY_TOP_OFFSET,
                          pb: 2,
                          overflowY: "auto",
                      }
                    : {}),
                ...sx,
            }}
        >
            {children ? (
                <Box
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        maxWidth: "100%",
                        width: "max-content",
                        ...contentSx,
                    }}
                >
                    {children}
                </Box>
            ) : null}
        </MotionBox>,
        document.body,
    );
}

BackdropOverlay.propTypes = {
    open: PropTypes.bool,
    onClick: PropTypes.func,
    children: PropTypes.node,
    mountWhenClosed: PropTypes.bool,
    zIndex: PropTypes.number,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
    contentSx: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
        PropTypes.func,
    ]),
    transition: PropTypes.object,
};
